import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useCollections } from '../store/CollectionContext';
import { usePatternContext } from '../store/PatternContext';
import { backPage, setCurrentCollectionId, setPage } from '../store/patternManagerSlice';
import { StoredCollection } from '../types/collection.model';

import LeftArrow from '../icons/LeftArrow';
import PlusIcon from '../icons/PlusIcon';
import PencilIcon from '../icons/PencilIcon';
import TrashIcon from '../icons/TrashIcon';
import Button from './Button';
import Card from './Card';
import InlineTextForm from './InlineTextForm';

const NewCollectionRow: React.FC<{ onCreate: (name: string) => Promise<void> }> = ({ onCreate }) => {
  const [creating, setCreating] = useState(false);

  if (!creating) {
    return (
      <Button onClick={() => setCreating(true)} Icon={<PlusIcon className="w-4 h-4" />} className="px-3 py-1.5">
        <p className="text-sm">New Collection</p>
      </Button>
    );
  }

  return (
    <InlineTextForm
      placeholder="Collection name"
      inputClassName="w-40"
      closeIconClassName="w-3.5 h-3.5"
      onConfirm={async (name) => { await onCreate(name); setCreating(false); }}
      onCancel={() => setCreating(false)}
    />
  );
};

const CollectionCard: React.FC<{
  collection: StoredCollection;
  patternCount: number;
  onOpen: () => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => void;
}> = ({ collection, patternCount, onOpen, onRename, onDelete }) => {
  const [renaming, setRenaming] = useState(false);

  return (
    <Card className="p-2 w-32 gap-1">
      {renaming ? (
        <InlineTextForm
          initialValue={collection.name}
          className="gap-1"
          inputClassName="px-1 py-0.5 text-xs w-20"
          closeIconClassName="w-3.5 h-3.5"
          onConfirm={async (name) => {
            if (name !== collection.name) await onRename(name);
            setRenaming(false);
          }}
          onCancel={() => setRenaming(false)}
        />
      ) : (
        <p className="text-sm text-center cursor-pointer truncate w-full" onClick={onOpen}>{collection.name}</p>
      )}
      <p className="text-xs text-gray-500 cursor-pointer" onClick={onOpen}>{patternCount} pattern{patternCount === 1 ? '' : 's'}</p>
      <div className="flex items-center gap-3 mt-1">
        <div className="cursor-pointer" onClick={() => setRenaming(true)}><PencilIcon /></div>
        <div className="cursor-pointer" onClick={onDelete}><TrashIcon /></div>
      </div>
    </Card>
  );
};

const CollectionList: React.FC = () => {
  const { collections, addCollection, renameCollection, deleteCollection } = useCollections();
  const { patterns, clearCollection } = usePatternContext();
  const dispatch = useDispatch();

  const back = () => dispatch(backPage());

  const openCollection = (id: string) => {
    dispatch(setCurrentCollectionId(id));
    dispatch(setPage('collection-detail'));
  };

  const patternCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const pattern of [...patterns.lightning, ...patterns['twisted-stripe']]) {
      if (pattern.collectionId === null) continue;
      counts.set(pattern.collectionId, (counts.get(pattern.collectionId) ?? 0) + 1);
    }
    return counts;
  }, [patterns]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this collection? Its patterns will become uncategorized.')) return;
    await deleteCollection(id);
    clearCollection(id);
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className='flex items-start w-full gap-2 mt-4 mb-2 px-4 cursor-pointer' onClick={back}>
        <div className="flex items-center"><LeftArrow /></div>
        <p className='text-sm'>All Pattern Types</p>
      </div>
      <div className="flex items-center gap-2 mt-2 mb-4">
        <p className="text-lg">Your Collections</p>
      </div>
      <div className="flex w-3/4 justify-center flex-wrap gap-3 mb-4">
        {collections.map(collection => (
          <CollectionCard
            key={collection.id}
            collection={collection}
            patternCount={patternCounts.get(collection.id) ?? 0}
            onOpen={() => openCollection(collection.id)}
            onRename={(name) => renameCollection(collection.id, name)}
            onDelete={() => handleDelete(collection.id)}
          />
        ))}
      </div>
      <NewCollectionRow onCreate={async (name) => { await addCollection(name); }} />
    </div>
  );
};

export default CollectionList;
